import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, X } from 'lucide-react';
import { superAdminAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const PLAN_COLORS = { basic: '#6B7280', pro: '#3B82F6', enterprise: '#8B5CF6' };
const STATUS_STYLES = {
  trial: { color: '#D97706', bg: '#FFFBEB' },
  active: { color: '#059669', bg: '#ECFDF5' },
  suspended: { color: '#DC2626', bg: '#FEF2F2' },
  cancelled: { color: '#9CA3AF', bg: '#F3F4F6' },
};

const TenantsPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [form, setForm] = useState({
    tenantName: '', adminFirstName: '', adminLastName: '',
    adminEmail: '', adminPassword: '', plan: 'basic'
  });

  const fetchTenants = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (planFilter) params.plan = planFilter;
      if (search) params.search = search;
      const res = await superAdminAPI.getTenants(params);
      setTenants(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, [search, statusFilter, planFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Map frontend form fields to backend expected fields
      const payload = {
        name: form.tenantName,
        email: form.adminEmail,
        plan: form.plan,
        adminFirstName: form.adminFirstName,
        adminLastName: form.adminLastName,
        adminPassword: form.adminPassword
      };
      const res = await superAdminAPI.createTenant(payload);
      toast.success(`Tenant "${form.tenantName}" created`);
      setShowForm(false);
      setForm({ tenantName: '', adminFirstName: '', adminLastName: '', adminEmail: '', adminPassword: '', plan: 'basic' });
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create tenant');
    }
  };

  const handleSuspend = async (id, name, isSuspended) => {
    if (!window.confirm(`${isSuspended ? 'Unsuspend' : 'Suspend'} "${name}"?`)) return;
    try {
      await superAdminAPI.suspendTenant(id, isSuspended ? 'Reactivated' : 'Suspended by admin');
      toast.success(`Tenant ${isSuspended ? 'unsuspended' : 'suspended'}`);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tenant Management</h1>
          <p className="page-subtitle">{pagination.total} registered companies</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={16} /> New Tenant
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 150 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select className="form-select" style={{ width: 140 }} value={planFilter} onChange={e => setPlanFilter(e.target.value)}>
          <option value="">All Plans</option>
          {['basic', 'pro', 'enterprise'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th><th>Email</th><th>Plan</th><th>Status</th>
                  <th>Properties</th><th>Users</th><th>Created</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 ? (
                  <tr><td colSpan={8}><div className="empty-state"><Building2 size={40} /><h3>No tenants found</h3></div></td></tr>
                ) : tenants.map(t => (
                  <tr key={t._id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>{t.subdomain}</div>
                    </td>
                    <td style={{ fontSize: 13 }}>{t.email}</td>
                    <td>
                      <span className="badge" style={{ color: PLAN_COLORS[t.subscription?.plan], background: `${PLAN_COLORS[t.subscription?.plan]}15` }}>
                        {t.subscription?.plan}
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={STATUS_STYLES[t.subscription?.status] || STATUS_STYLES.trial}>
                        {t.subscription?.status}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600 }}>{t.usage?.propertiesCount || 0}</span>
                      <span style={{ color: '#9CA3AF', fontSize: 12 }}> / {t.limits?.maxProperties === 999 ? '∞' : t.limits?.maxProperties}</span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600 }}>{t.usage?.usersCount || 0}</span>
                      <span style={{ color: '#9CA3AF', fontSize: 12 }}> / {t.limits?.maxUsers === 999 ? '∞' : t.limits?.maxUsers}</span>
                    </td>
                    <td style={{ fontSize: 13, color: '#6B7280' }}>
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/super-admin/tenants/${t._id}`)}>View</button>
                        <button
                          className={`btn btn-sm ${t.subscription?.status === 'suspended' ? 'btn-success' : 'btn-danger'}`}
                          onClick={() => handleSuspend(t._id, t.name, t.subscription?.status === 'suspended')}
                        >
                          {t.subscription?.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-secondary'}`} onClick={() => fetchTenants(p)}>{p}</button>
          ))}
        </div>
      )}

      {/* Create Tenant Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Create New Tenant</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ padding: '12px 14px', background: '#F0F9FF', borderRadius: 8, fontSize: 13, color: '#1D4ED8' }}>
                  This will create a new tenant account and their first admin user.
                </div>
                <div className="form-group">
                  <label className="form-label">Company Name *</label>
                  <input className="form-input" placeholder="Acme Rentals" value={form.tenantName} onChange={e => setForm(p => ({ ...p, tenantName: e.target.value }))} required />
                </div>
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Admin First Name *</label>
                    <input className="form-input" value={form.adminFirstName} onChange={e => setForm(p => ({ ...p, adminFirstName: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Admin Last Name *</label>
                    <input className="form-input" value={form.adminLastName} onChange={e => setForm(p => ({ ...p, adminLastName: e.target.value }))} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Admin Email *</label>
                  <input type="email" className="form-input" value={form.adminEmail} onChange={e => setForm(p => ({ ...p, adminEmail: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Temporary Password *</label>
                  <input type="password" className="form-input" minLength={6} value={form.adminPassword} onChange={e => setForm(p => ({ ...p, adminPassword: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Starting Plan</label>
                  <select className="form-select" value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}>
                    <option value="basic">Basic — $29/mo</option>
                    <option value="pro">Pro — $79/mo</option>
                    <option value="enterprise">Enterprise — $199/mo</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Tenant</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
